import { getAllOrganizations, IFilter, IOrganization } from "@/api";
import { ORGANIZATION_QUERY_FILTER } from "@/config/constants";
import { useDebouncedEffect } from "@react-hookz/web";
import { FormAutoCompleteElement } from "@rhf-kit/mui";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

/**
 * The autocomplete input for organizations
 */
interface Props {
    multiple?: boolean;
    filter?: boolean;
    required?: boolean;
}

export const OrganizationsAutocomplete = ({
    multiple = true,
    filter = true,
    required = true,
}: Props) => {
    const [organizationOptions, setOrganizationOptions] = useState<
        IOrganization[]
    >([]);

    const [search, setSearch] = useState<string>("");
    const [queryFilter, setQueryFilter] = useState<IFilter>(
        ORGANIZATION_QUERY_FILTER
    );

    const { data, isPending } = useQuery({
        queryKey: ["organizations-autocomplete", queryFilter],
        queryFn: () => getAllOrganizations(1, 100, queryFilter),
    });

    useEffect(() => {
        if (data) {
            setOrganizationOptions(data.items);
        }
    }, [data]);

    useDebouncedEffect(
        () => {
            if (!filter) {
                return;
            }
            if (search === "") {
                setQueryFilter(ORGANIZATION_QUERY_FILTER);
                return;
            }

            setQueryFilter({
                ...ORGANIZATION_QUERY_FILTER,
                q: search,
            });
        },
        [search],
        250,
        1000
    );

    return (
        <FormAutoCompleteElement
            name={multiple ? "organizations" : "organization"}
            label={multiple ? "Organizations" : "Organization"}
            loading={isPending}
            options={organizationOptions}
            getOptionLabel={(option) =>
                typeof option === "string" ? option : option.name
            }
            autoComplete
            includeInputInList
            filterSelectedOptions
            onInputChange={(_, newSearchValue) => setSearch(newSearchValue)}
            filterOptions={(x) => x}
            fullWidth
            multiple={multiple}
            required={required}
        />
    );
};
